// Client-side persistence for Codeacademy AI chat sessions.
//
// The scraper stores every session's messages server-side, but sessions are
// anonymous (no user column) and there is no endpoint to list a user's sessions
// or fetch a transcript by session_id. So we keep a per-user copy in
// localStorage: one JSON blob per Firebase uid, holding the list of sessions and
// their transcripts. This is what lets a conversation survive a refresh and lets
// the user keep and switch between several chats.
//
// Everything here is pure and defensive: reads validate shape (foreign/corrupt
// values fall back to an empty store) and writes are wrapped so a disabled or
// full localStorage never throws into the UI.

const KEY_PREFIX = 'kursuok_chat:';
// Cap stored sessions so a heavy user can't grow the blob past the ~5MB origin
// quota. Oldest (by updatedAt) are dropped first; the active one is always kept.
const MAX_SESSIONS = 20;
const TITLE_MAX = 40;

export interface StoredMessage {
    role: 'user' | 'assistant';
    content: string;
}

export interface ChatSession {
    id: string; // client-generated session_id; also the React key. The backend
    // creates the matching conversation row lazily on the first real message.
    title: string; // '' until the first real user message
    createdAt: number;
    updatedAt: number;
    messages: StoredMessage[]; // greeting is messages[0], a canned local message
}

export interface ChatStore {
    activeSessionId: string | null;
    sessions: ChatSession[];
}

// ----------------------------------------------------------------------

export const emptyStore = (): ChatStore => ({ activeSessionId: null, sessions: [] });

const storageKey = (uid: string): string => `${KEY_PREFIX}${uid}`;

const isMessage = (value: unknown): value is StoredMessage => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const message = value as Record<string, unknown>;
    return (message.role === 'user' || message.role === 'assistant') && typeof message.content === 'string';
};

const isSession = (value: unknown): value is ChatSession => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const session = value as Record<string, unknown>;
    return (
        typeof session.id === 'string' &&
        typeof session.title === 'string' &&
        typeof session.createdAt === 'number' &&
        typeof session.updatedAt === 'number' &&
        Array.isArray(session.messages) &&
        session.messages.every(isMessage)
    );
};

// Collapse whitespace and clip to a label-sized string for the session list.
export const truncateTitle = (text: string): string => {
    const clean = text.trim().replace(/\s+/g, ' ');
    return clean.length > TITLE_MAX ? `${clean.slice(0, TITLE_MAX).trimEnd()}…` : clean;
};

// Immutably append a message to one session, bumping updatedAt and — the first
// time a user message lands — deriving the session title from it. (The first
// message of a session is the discarded primer, so the first *user* bubble is
// genuinely the user's first question.)
export const upsertMessage = (
    sessions: ChatSession[],
    id: string,
    message: StoredMessage,
    titleIfEmpty?: string,
): ChatSession[] =>
    sessions.map(session => {
        if (session.id !== id) {
            return session;
        }
        const title =
            !session.title && message.role === 'user' && titleIfEmpty ? truncateTitle(titleIfEmpty) : session.title;
        return { ...session, title, updatedAt: Date.now(), messages: [...session.messages, message] };
    });

// Keep at most MAX_SESSIONS, dropping the oldest by updatedAt but never the
// active one. Under the cap the array is returned unchanged (order preserved).
export const capSessions = (sessions: ChatSession[], activeId: string | null): ChatSession[] => {
    if (sessions.length <= MAX_SESSIONS) {
        return sessions;
    }
    const byRecency = [...sessions].sort((a, b) => b.updatedAt - a.updatedAt);
    const kept = byRecency.slice(0, MAX_SESSIONS);
    if (activeId && !kept.some(session => session.id === activeId)) {
        const active = sessions.find(session => session.id === activeId);
        if (active) {
            kept[kept.length - 1] = active;
        }
    }
    return kept;
};

export const loadStore = (uid: string): ChatStore => {
    try {
        const raw = localStorage.getItem(storageKey(uid));
        if (!raw) {
            return emptyStore();
        }
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.sessions)) {
            return emptyStore();
        }
        const sessions = parsed.sessions.filter(isSession);
        const activeSessionId =
            typeof parsed.activeSessionId === 'string' &&
            sessions.some(session => session.id === parsed.activeSessionId)
                ? parsed.activeSessionId
                : sessions[0]?.id ?? null;
        return { activeSessionId, sessions };
    } catch {
        // Disabled storage, quota, or malformed JSON — start clean rather than throw.
        return emptyStore();
    }
};

export const saveStore = (uid: string, store: ChatStore): void => {
    const key = storageKey(uid);
    let sessions = capSessions(store.sessions, store.activeSessionId);
    // Retry loop for QuotaExceededError: shed the oldest non-active session and
    // try again until it fits or nothing more can be dropped, then give up
    // silently (the in-memory copy still works for this visit).
    for (;;) {
        try {
            localStorage.setItem(key, JSON.stringify({ activeSessionId: store.activeSessionId, sessions }));
            return;
        } catch {
            let oldestIndex = -1;
            let oldestAt = Infinity;
            sessions.forEach((session, index) => {
                if (session.id !== store.activeSessionId && session.updatedAt < oldestAt) {
                    oldestAt = session.updatedAt;
                    oldestIndex = index;
                }
            });
            if (oldestIndex === -1) {
                return;
            }
            sessions = sessions.filter((_, index) => index !== oldestIndex);
        }
    }
};

// ----------------------------------------------------------------------
// Consent — separate from the session data above on purpose: it's a one-time
// per-user decision, not a transcript. Recorded once, per uid, closed by
// default (the intro screen's checkbox starts unchecked; there is no implicit
// consent from merely opening the panel or clicking "New chat").
const CONSENT_KEY_PREFIX = 'kursuok_chat_consent:';

export const hasGivenChatConsent = (uid: string): boolean => {
    try {
        return localStorage.getItem(`${CONSENT_KEY_PREFIX}${uid}`) === 'true';
    } catch {
        return false;
    }
};

export const recordChatConsent = (uid: string): void => {
    try {
        localStorage.setItem(`${CONSENT_KEY_PREFIX}${uid}`, 'true');
    } catch {
        // ignore — storage disabled; consent will simply be asked again next time
    }
};
